import React, { useState, useEffect, useRef, useMemo } from 'react'
import AssetService from "../../services/AssetService";
import FacilityService from "../../services/FacilityService";
import UserService from "../../services/UserService";
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import util from "../../utils/util";
import moment from "moment";
import AssetInventoryService from '../../services/AssetInventoryService';
import { saveAs } from 'file-saver';
import excel from "../assets/icons/excel.png"; 
import acrobat from "../assets/icons/acrobat.png";
import search from "../assets/icons/magnifying-glass-solid.svg"
import Dueforservice from '../../utils/Dueforservice';

import {
    Input,
    Button,
    message,
    Card,
    Statistic,
    Progress,
    Row,
    Col,
    Table,
    Tag,
    Select,
    DatePicker,
    Alert
} from 'antd';

import {
    BarChartOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ToolOutlined,
    CalendarOutlined,
    WarningOutlined,
    RiseOutlined,
    FallOutlined,
    SafetyOutlined,
    SyncOutlined
} from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Service = () => {
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [infrastuctureSubCats, setInfrastuctureSubCats] = useState([]);
    const [assets, setAssets] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [showAnalytics, setShowAnalytics] = useState(true);
    const [analyticsDateRange, setAnalyticsDateRange] = useState([null, null]);
    const [selectedAnalyticsType, setSelectedAnalyticsType] = useState('all');

    const [result, setResult] = useState({ data: [], page: {} });
    const sdataRef = useRef({ p: 1, ps: 25 });
    const isNational = util.isNational();

    // Enhanced Analytics Calculations
    const analytics = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Filter equipment based on analytics filters
        const filteredEquipment = result.data.filter(equipment => {
            const installationDate = new Date(equipment.installation_date);
            const matchesDateRange = !analyticsDateRange[0] || !analyticsDateRange[1] || 
                (installationDate >= analyticsDateRange[0] && installationDate <= analyticsDateRange[1]);
            const matchesType = selectedAnalyticsType === 'all' || equipment.item.toLowerCase().includes(selectedAnalyticsType.toLowerCase());
            return matchesDateRange && matchesType;
        });

        // Basic KPIs
        const totalEquipment = filteredEquipment.length;
        const totalValue = filteredEquipment.reduce((sum, eq) => sum + parseFloat(eq.cost || 0), 0);

        // Service Due Analysis
        const serviceDueToday = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            return nextServiceDate.toDateString() === today.toDateString();
        }).length;

        const serviceDueIn7Days = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            const daysUntilService = (nextServiceDate - now) / (1000 * 60 * 60 * 24);
            return daysUntilService <= 7 && daysUntilService >= 0;
        }).length;

        const serviceDueIn30Days = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            const daysUntilService = (nextServiceDate - now) / (1000 * 60 * 60 * 24);
            return daysUntilService <= 30 && daysUntilService >= 0;
        }).length;

        const overdueService = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            return nextServiceDate < now;
        }).length;

        // Functional Status Analysis
        const functionalEquipment = filteredEquipment.filter(eq => 
            eq.functional_status === 'Functional' || eq.functional_status === 'Working').length;
        const functionalRate = totalEquipment > 0 ? (functionalEquipment / totalEquipment * 100) : 0;

        // Service Compliance Rate
        const serviceCompliance = totalEquipment > 0 ? ((totalEquipment - overdueService) / totalEquipment * 100) : 100;

        // Critical Service Items
        const criticalServiceItems = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return true;
            const nextServiceDate = new Date(eq.next_year_date);
            const daysUntilService = (nextServiceDate - now) / (1000 * 60 * 60 * 24);
            const isOverdue = nextServiceDate < now;
            const isDueVeryClose = daysUntilService <= 7 && daysUntilService >= 0;
            return isOverdue || isDueVeryClose;
        }).slice(0, 10);

        return {
            totalEquipment,
            totalValue,
            serviceDueToday,
            serviceDueIn7Days,
            serviceDueIn30Days,
            overdueService,
            functionalRate,
            serviceCompliance,
            criticalServiceItems
        };
    }, [result.data, analyticsDateRange, selectedAnalyticsType]);

    const list4 = (p, ps, getAssignedToUsers) => {
        const ids = new URLSearchParams(window.location.search).get('ids');
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.ids = ids || '';
        
        // For national users showing analytics, ensure we get ALL data
        if (isNational === 1 && showAnalytics) {
            const originalClientId = sdataRef.current.client_id;
            sdataRef.current.client_id = null;
            
            util.showLoader();
            AssetInventoryService.lists4(sdataRef.current, getAssignedToUsers).then(({ data }) => {
                setResult({ ...data.result });
                if (data.assigned_users) {
                    setAssignedUsers(data.assigned_users);
                }
                sdataRef.current.client_id = originalClientId;
            }).catch(e => {
                message.error(e.message);
                sdataRef.current.client_id = originalClientId;
            }).finally(() => {
                util.hideLoader();
            });
        } else {
            util.showLoader();
            AssetInventoryService.lists4(sdataRef.current, getAssignedToUsers).then(({ data }) => {
                setResult({ ...data.result });
                if (data.assigned_users) {
                    setAssignedUsers(data.assigned_users);
                }
            }).catch(e => {
                message.error(e.message);
            }).finally(() => {
                util.hideLoader();
            });
        }
    }

    useEffect(() => {
        list4(0, 0, 'getAssignedToUsers');

        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => ({ id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me }));
            setClients(allClients);
        });

        UserService.allUsers({ status: 1 }).then(res => {
            let allUsers = res.data.result.data.map(v => ({ id: v.id, name: v.name, role: v.role }));
            setUsers(allUsers);
        });

        AssetService.infrastuctureSubCats({ status: 1 }).then(res => {
            setInfrastuctureSubCats(res.data.result);
        });

        AssetService.all({ cat_type: 'Med/NonMed', my_only: 'Y' }).then(res => {
            setAssets([...res.data.result.data.map((v) => ({ id: v.id, name: ((v.serial_no ? (v.serial_no + ' - ') : '') + v.item_full_name) + ' [' + v.cat + ']' }))]);
        });

        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints([...res.data.result.data]);
        });

        return () => { message.destroy() }
    }, []);

    const downloadPdf = () => {
        Dueforservice.generateStockTransactionsReport(sdataRef, util.Orientation.LANSCAPE);  
    }

    const download = () => {
        sdataRef.current.p = 1;
        sdataRef.current.ps = 0;
        util.showLoader();
        AssetInventoryService.lists4(sdataRef.current).then(({ data }) => {
            let header = {
                client: 'FACILITY',
                item: 'EQUIPMENT NAME',
                serial_no: 'SERIAL NUMBER',
                cost: 'VALUE',
                functional_status: 'FUNCTIONAL _STATUS',
                installation_date: 'INSTALLATION DATE',
                next_year_date: 'NEXT ROUTINE SERVICE DATE',
            };

            let blob = util.convertToCSVBlob(data.result.data, header);
            saveAs(blob, "routine-service-report.csv");
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    function calculateMaturityDate(installationDate, lifespanValue) {
        const date = new Date(installationDate);
        const lifespanYears = parseInt(lifespanValue, 10);
        date.setFullYear(date.getFullYear() + lifespanYears);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
    }
    
    function calculateRemainingYears(installationDate, lifespanValue) {
        const maturityDate = new Date(installationDate);
        const lifespanYears = parseInt(lifespanValue, 10);
        maturityDate.setFullYear(maturityDate.getFullYear() + lifespanYears);
        const currentDate = new Date();
        const remainingYears = maturityDate.getFullYear() - currentDate.getFullYear();
        return remainingYears;
    }
            
    function calculateRemainingMonths(installationDate, lifespanValue) {
        const maturityDate = new Date(installationDate);
        const lifespanYears = parseInt(lifespanValue, 10);
        maturityDate.setFullYear(maturityDate.getFullYear() + lifespanYears);
        const currentDate = new Date();
        const remainingMonths = (maturityDate.getFullYear() - currentDate.getFullYear()) * 12 + (maturityDate.getMonth() - currentDate.getMonth());
        return remainingMonths;
    }

    const renderAnalyticsDashboard = () => (
        <div className="analytics-dashboard" style={{ marginBottom: 24, padding: 16, backgroundColor: '#f0f9ff', borderRadius: 6 }}>
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button 
                    onClick={() => setShowAnalytics(!showAnalytics)}
                    icon={showAnalytics ? <FallOutlined /> : <BarChartOutlined />}
                >
                    {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                </Button>
            </div>

            {showAnalytics && (
                <>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <RangePicker 
                                onChange={setAnalyticsDateRange}
                                style={{ width: '100%' }}
                                placeholder={['Installation From', 'Installation To']}
                            />
                        </Col>
                        <Col span={12}>
                            <Select 
                                value={selectedAnalyticsType} 
                                onChange={setSelectedAnalyticsType}
                                style={{ width: '100%' }}
                                placeholder="Filter by Equipment Type"
                            >
                            </Select>
                        </Col>
                    </Row>

                    {(analytics.overdueService > 0 || analytics.serviceDueToday > 0) && (
                        <Alert
                            message="Service Alerts"
                            description={
                                <div>
                                    {analytics.overdueService > 0 && <Tag color="red">Overdue: {analytics.overdueService}</Tag>}
                                    {analytics.serviceDueToday > 0 && <Tag color="orange">Due Today: {analytics.serviceDueToday}</Tag>}
                                    {analytics.serviceDueIn7Days > 0 && <Tag color="yellow">Due This Week: {analytics.serviceDueIn7Days}</Tag>}
                                </div>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                    )}

                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Total Equipment"
                                    value={analytics.totalEquipment}
                                    prefix={<ToolOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Service Compliance"
                                    value={analytics.serviceCompliance}
                                    suffix="%"
                                    precision={1}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: analytics.serviceCompliance > 95 ? '#3f8600' : analytics.serviceCompliance > 85 ? '#fa8c16' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Overdue Service"
                                    value={analytics.overdueService}
                                    prefix={<ExclamationCircleOutlined />}
                                    valueStyle={{ color: '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Portfolio Value"
                                    value={analytics.totalValue}
                                    prefix="E"
                                    precision={0}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Due Today"
                                    value={analytics.serviceDueToday}
                                    prefix={<CalendarOutlined />}
                                    valueStyle={{ color: analytics.serviceDueToday > 0 ? '#fa8c16' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Due This Week"
                                    value={analytics.serviceDueIn7Days}
                                    prefix={<ClockCircleOutlined />}
                                    valueStyle={{ color: analytics.serviceDueIn7Days > 0 ? '#fa8c16' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Due This Month"
                                    value={analytics.serviceDueIn30Days}
                                    prefix={<SyncOutlined />}
                                    valueStyle={{ color: analytics.serviceDueIn30Days > 0 ? '#1890ff' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Functional Rate"
                                    value={analytics.functionalRate}
                                    suffix="%"
                                    precision={1}
                                    prefix={<SafetyOutlined />}
                                    valueStyle={{ color: analytics.functionalRate > 90 ? '#3f8600' : analytics.functionalRate > 80 ? '#fa8c16' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {analytics.criticalServiceItems.length > 0 && (
                        <Row gutter={16}>
                            <Col span={24}>
                                <Card title="Critical Service Items - Immediate Attention Required">
                                    <Table
                                        dataSource={analytics.criticalServiceItems}
                                        columns={[
                                            { title: 'Equipment', dataIndex: 'item', key: 'item' },
                                            { title: 'Serial No', dataIndex: 'serial_no', key: 'serial_no' },
                                            { 
                                                title: 'Next Service', 
                                                dataIndex: 'next_year_date', 
                                                key: 'next_year_date',
                                                render: (date) => {
                                                    if (!date) return <Tag color="gray">Not Scheduled</Tag>;
                                                    const nextService = new Date(date);
                                                    const now = new Date();
                                                    const isOverdue = nextService < now;
                                                    const daysUntil = Math.ceil((nextService - now) / (1000 * 60 * 60 * 24));
                                                    
                                                    return (
                                                        <div>
                                                            <div>{date}</div>
                                                            <Tag color={isOverdue ? 'red' : daysUntil <= 7 ? 'orange' : 'yellow'}>
                                                                {isOverdue ? `${Math.abs(daysUntil)} days overdue` : 
                                                                 daysUntil <= 0 ? 'Due today' :
                                                                 `${daysUntil} days left`}
                                                            </Tag>
                                                        </div>
                                                    );
                                                }
                                            },
                                            { 
                                                title: 'Value', 
                                                dataIndex: 'cost', 
                                                key: 'cost',
                                                render: (cost) => `E${parseFloat(cost || 0).toLocaleString()}`
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                        rowKey="serial_no"
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Routine Equipment Service Report</h2>
                </div>
                <div className="my-auto ml-auto"></div>
            </div>

            {renderAnalyticsDashboard()}

            <div className="page-pad">
                {result.data.length > 0 &&
                    <div className="text-secondary mb8">
                        Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }

                <div className="d-flex tbl-search-head">
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm
                            dataRef={sdataRef}
                            onSearch={list4}
                            clients={clients}
                            assignedUsers={assignedUsers}
                            isNational={isNational}
                        />
                    </div>
                    
                    <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={download} 
                            style={{
                                backgroundColor: 'green', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={excel} alt="download" height={15} /> Export Excel
                        </button>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={downloadPdf} 
                            style={{
                                backgroundColor: 'red', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={acrobat} alt="download" height={15} /> Export PDF
                        </button>
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w20">SN</th>
                                    {isNational === 1 && <th className="w200">Facility</th>}
                                    <th className="w500">EQUIPMENT NAME</th>
                                    <th className="w500">SERIAL NUMBER</th>
                                    <th>VALUE: SZL</th>
                                    <th>FUNCTIONAL STATUS</th>
                                    <th className="w200">DEPLOYMENT DATE</th>
                                    <th className="w200">ROUTINE NEXT SERVICE</th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {result.data.map((v, i) => {
                                    const nextServiceDate = new Date(v.next_year_date);
                                    const currentDate = new Date();
                                    const timeDifference = nextServiceDate - currentDate;
                                    const daysDifference = timeDifference / (1000 * 3600 * 24);
                                    const isLessThan30Days = daysDifference < 30;

                                    return (
                                        <tr key={i}>
                                            <td>{i + 1}.</td>
                                            {isNational === 1 && <td>{v.client}</td>}
                                            <td>
                                                <div className="uc bold600">{v.item}</div>
                                                <div className="d-flex">
                                                    <div className="w90">Service</div> : 
                                                    <div className="bold600 pl5">Every {v.service_period} {v.service_period_type}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w90">Life Span</div> : 
                                                    <div className="bold600 pl5">{v.lifespanvalue} {v.lifespanunit}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w90">Maturity Date</div> : 
                                                    <div className="bold600 pl5">{calculateMaturityDate(v.installation_date,v.lifespanvalue)}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w90">Remaining Time</div> : 
                                                    <div className="bold600 pl5">{calculateRemainingYears(v.installation_date,v.lifespanvalue)} Years or {calculateRemainingMonths(v.installation_date,v.lifespanvalue)} Months</div>
                                                </div>
                                            </td>
                                            <td>{v.serial_no}</td>
                                            <td>{v.cost}</td>
                                            <td>{v.functional_status}</td>
                                            <td>{v.installation_date}</td>
                                            <td style={{ color: isLessThan30Days ? 'red' : 'inherit' }}>{v.next_year_date}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging
                            onChange={list4}
                            total={result.page.total_records}
                            current={result.page.cur_page}
                            pageSize={sdataRef.current.ps}
                            showSizeChanger
                        />
                    </div>
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>
        </div>
    );
};

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, isNational } = props;
    let [data, setData] = useState({ ...dataRef.current });
    const [items, setItems] = useState([]);

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.assigned_to = null;
        }
        setData({ ...data });
    }

    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        let ob = clients.find(v => v.is_me);
        setData({ ...data, client_id: ob?.id });
    }, [clients]);

    useEffect(() => {
        AssetService.allItems({status:1}).then(res=>{
            let allItems=[];
            allItems=res.data.result.data.map((v)=>{
                let item_full_name=v.item_full_name+' ['+(v.cat_type==='Infrastructure'?v.subcat:v.cat)+']';
                return {label:item_full_name, value:v.id};
            });
            setItems(allItems);
        });

        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 &&
                    <div className="w150 mr5">
                        <AntdSelect
                            placeholder="Facility (All)"
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => handleChange(v, 'client_id')}
                        />
                    </div>
                }

                <div className="w500 mr5">
                    <AntdSelect 
                        placeholder="Item (All)" 
                        allowClear 
                        showSearch 
                        options={items} 
                        value={data.item_id} 
                        onChange={v=>handleChange(v, 'item_id')} 
                    />
                </div>

                <div className="w300 mr5">
                    <AntdDatepicker
                        placeholder="From Date"
                        allowClear
                        value={data.from_date}
                        onChange={v => {
                            if (data.to_date && moment(v) > moment(data.to_date)) {
                                message.error("From date should be less than or equal to To Date");
                                v = null;
                            }
                            handleChange(v, 'from_date');
                        }}
                    />
                </div>

                <div className="w300 mr5">
                    <AntdDatepicker
                        placeholder="To Date"
                        allowClear
                        value={data.to_date}
                        onChange={v => {
                            if (data.from_date && moment(v) < moment(data.from_date)) {
                                message.error("To date should be greater than or equal to From Date");
                                v = null;
                            }
                            handleChange(v, 'to_date');
                        }}
                    />
                </div>

                <Button className="mt3" type="primary" onClick={() => onSearch()}>
                    <img src={search} alt='' height={15}/>
                </Button>
            </div>
        </form>
    )
}

export default Service